import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Users, Smile, MessageSquare, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { useToast } from "./ui/use-toast";

// Define the Message type based on our database schema
interface Message {
  id: string;
  username: string;
  content: string;
  created_at: string;
  room_id: string;
  user_id?: string;
  updated_at?: string;
  isSending?: boolean; // Flag to identify messages being sent
}

interface ChatPanelProps {
  participantsCount: number;
}

export const ChatPanel = ({ participantsCount }: ChatPanelProps) => {
  // Unique id for this ChatPanel instance to avoid Supabase duplicate subscriptions
  const channelIdRef = useRef(`chat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Get current user and room ID from URL
  const searchParams = new URLSearchParams(window.location.search);
  const roomId = searchParams.get('room');
  const currentUser = searchParams.get('user') || 'Anonymous';
  
  const onlineUsers = participantsCount;
  
  // Fetch existing messages and set up real-time subscription
  useEffect(() => {
    if (!roomId) return;
    
    console.log('Setting up message subscription for room:', roomId);
    
    const fetchMessages = async () => {
      try {
        setLoading(true);
        console.log('Fetching existing messages...');
        
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('room_id', roomId)
          .order('created_at', { ascending: true });
          
        if (error) throw error;
        
        console.log('Fetched messages:', data);
        const messages = (data || []) as unknown as Message[];
        setMessages(messages);
      } catch (error) {
        console.error('Error fetching messages:', error);
        toast({
          title: 'Error',
          description: 'Failed to load messages',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
    
    // Set up real-time subscription using the Supabase client
    const channelName = `room:${roomId}`;
    console.log('Creating realtime channel:', channelName);
    
    // Channel for database changes
    const dbChannel = supabase.channel(`${channelIdRef.current}-broadcast`, {
      config: {
        broadcast: { ack: true, self: true }
      }
    });
    
    // Subscribe to new messages
    const subscription = dbChannel
      .on('broadcast', { event: 'new_message' }, (payload) => {
        console.log('New message notification received:', payload);
        
        if (payload.payload.room_id === roomId) {
          console.log('New message for this room, processing...');
          // Instead of refetching all messages, we'll handle the new message directly
          // The message will be added to the UI through the postgres_changes subscription
        }
      })
      .subscribe((status, err) => {
        console.log('Broadcast subscription status:', status);
        
        if (err) {
          console.error('Broadcast subscription error:', err);
          toast({
            title: 'Connection Error',
            description: 'Failed to connect to real-time updates',
            variant: 'destructive',
          });
          return;
        }
        
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to broadcast channel');
          
          // Set up postgres changes to handle new messages
          const postgresChannel = supabase.channel(`${channelIdRef.current}-pg`)
            .on(
              'postgres_changes',
              {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `room_id=eq.${roomId}`
              },
              (payload) => {
                console.log('New message detected:', payload);
                if (payload.new) {
                  const newMessage: Message = {
                    id: String(payload.new.id),
                    username: String(payload.new.username || 'Anonymous'),
                    content: String(payload.new.content),
                    created_at: payload.new.created_at || new Date().toISOString(),
                    room_id: String(payload.new.room_id),
                    user_id: payload.new.user_id ? String(payload.new.user_id) : undefined,
                    updated_at: payload.new.updated_at
                  };
                  
                  console.log('Adding new message to UI:', newMessage);
                  
                  // Use functional update to ensure we have the latest state
                  setMessages(prevMessages => {
                    // Check if message already exists to avoid duplicates
                    const messageExists = prevMessages.some(msg => 
                      msg.id === newMessage.id || 
                      (msg.content === newMessage.content && 
                       msg.username === newMessage.username &&
                       Math.abs(
                         new Date(msg.created_at).getTime() - 
                         new Date(newMessage.created_at).getTime()
                       ) < 1000)
                    );
                    
                    if (!messageExists) {
                      console.log('Message is new, adding to UI');
                      return [...prevMessages, newMessage];
                    }
                    
                    console.log('Message already exists, skipping');
                    return prevMessages;
                  });
                }
              }
            )
            .subscribe((status, err) => {
              console.log('Postgres subscription status:', status);
              if (err) console.error('Postgres subscription error:', err);
            });
            
          // Store the postgres channel for cleanup
          return () => postgresChannel.unsubscribe();
        }
      });
    
    // Cleanup function
    return () => {
      console.log('Cleaning up subscriptions');
      subscription.unsubscribe();
    };
  }, [roomId, currentUser, toast]);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Function to focus the input
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  }, []);
  
  // Focus the input when the component mounts
  useEffect(() => {
    focusInput();
  }, [focusInput]);
  
  const sendMessage = async () => {
    const messageText = newMessage.trim();
    if (!messageText || !roomId) return;
    
    // Create a temporary message ID for optimistic UI update
    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();
    
    // Clear the input immediately for better UX
    setNewMessage('');
    // Focus the input after state update
    focusInput();
    
    // Create the message object for optimistic update
    const tempMessage: Message = {
      id: tempId,
      room_id: roomId,
      content: messageText,
      username: currentUser,
      created_at: now,
      updated_at: now,
      isSending: true // Add a flag to identify messages being sent
    };
    
    // Optimistically add the message to the UI
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    
    try {
      setSending(true);
      console.log('Sending message:', { roomId, message: messageText, user: currentUser });
      
      // Use direct insert instead of RPC for better real-time support
      const { data, error } = await supabase
        .from('messages')
        .insert([{
          room_id: roomId,
          content: messageText,
          username: currentUser,
          created_at: now
        }])
        .select();
      
      if (error) throw error;
      
      console.log('Message sent successfully:', data);
      
      // Focus input again after message is processed
      focusInput();
      
      // Update the message with the real ID from the server
      if (data && data[0]) {
        const serverMessage = data[0];
        
        setMessages(prev => 
          prev.map(msg => {
            if (msg.id === tempId) {
              const updatedMessage: Message = {
                ...msg,
                id: String(serverMessage.id || msg.id),
                created_at: typeof serverMessage.created_at === 'string' ? serverMessage.created_at : msg.created_at,
                updated_at: typeof serverMessage.updated_at === 'string' ? serverMessage.updated_at : new Date().toISOString(),
                room_id: String(serverMessage.room_id || msg.room_id),
                username: String(serverMessage.username || msg.username),
                content: String(serverMessage.content || msg.content),
                user_id: serverMessage.user_id ? String(serverMessage.user_id) : msg.user_id
              };
              
              // Remove the isSending flag
              const { isSending, ...finalMessage } = updatedMessage;
              
              // Broadcast the new message to other clients
              supabase.channel('db-changes')
                .send({
                  type: 'broadcast',
                  event: 'new_message',
                  payload: {
                    room_id: finalMessage.room_id,
                    message_id: finalMessage.id
                  }
                })
                .then(() => console.log('Broadcasted new message notification'))
                .catch(err => console.error('Error broadcasting message:', err));
                
              return finalMessage;
            }
            return msg;
          })
        );
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove the optimistic update if there was an error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      
      toast({
        title: 'Error',
        description: 'Failed to send message: ' + (error as Error).message,
        variant: 'destructive',
      });
      
      // Restore the message in the input field
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Focus the input when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Focus the input when the panel is opened
  const handlePanelClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending) {
        sendMessage();
      }
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      className="h-full flex flex-col bg-white/70 border-l border-black/10 relative"
      onClick={handlePanelClick}
    >
      {/* Hand-drawn border effect */}
      <div className="absolute inset-0 border-l-2 border-black/5 pointer-events-none" style={{
        borderImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0,0 L100,0 L100,100 L0,100 Z\' fill=\'none\' stroke=\'rgba(0,0,0,0.1)\' stroke-width=\'2\' stroke-dasharray=\'6,4\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") 2',
        borderImageWidth: '0 0 0 2px',
        borderImageOutset: '0 0 0 1',
        borderImageRepeat: 'stretch',
      }} />
      
      {/* Header */}
      <div className="p-4 border-b border-black/10 bg-white/90 backdrop-blur-sm relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-black/5 rounded-lg text-black/80">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-medium text-black">Team Chat</h3>
              <p className="text-xs text-black/60 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></span>
                {onlineUsers} {onlineUsers === 1 ? 'person' : 'people'} online
              </p>
            </div>
          </div>
          <div className="text-xs bg-black/5 text-black/60 px-2.5 py-1 rounded-full border border-black/10 font-medium">
            {messages.length} {messages.length === 1 ? 'message' : 'messages'}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-white/50 scrollbar-thin scrollbar-thumb-black/20 scrollbar-track-transparent hover:scrollbar-thumb-black/30 relative">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-black/40" />
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-black/40 text-sm">
            <div className="p-3 bg-black/5 rounded-full mb-3">
              <MessageSquare className="w-5 h-5" />
            </div>
            <p className="font-medium text-black/60">No messages yet</p>
            <div className="text-xs text-gray-500 mt-1">
              {onlineUsers} {onlineUsers === 1 ? 'person' : 'people'} online
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "flex items-start space-x-3 group w-full",
                message.username === currentUser ? "justify-end" : "justify-start"
              )}
            >
              {message.username !== currentUser && (
                <div 
                  className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-xs font-medium text-black/60 flex-shrink-0"
                  title={message.username}
                >
                  {message.username.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col max-w-[70%]">
                {message.username !== currentUser && (
                  <div className="flex items-center mb-1">
                    <span className="text-xs font-medium text-gray-700">
                      {message.username}
                    </span>
                    {message.isSending && (
                      <div className="ml-2 h-2 w-2 rounded-full bg-blue-400 animate-ping" />
                    )}
                  </div>
                )}
                <div className={cn(
                  "px-4 py-2.5 rounded-2xl",
                  message.username === currentUser
                    ? "bg-blue-500 text-white rounded-br-none"
                    : "bg-black/5 text-black rounded-bl-none",
                  message.isSending && "opacity-70"
                )}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="flex items-center justify-end mt-1 space-x-2">
                    {message.isSending && (
                      <span className="text-xs opacity-70">Sending...</span>
                    )}
                    <span className="text-xs opacity-70">
                      {formatTime(message.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              {message.username === currentUser && message.isSending && (
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
                </div>
              )}
            </motion.div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-100 bg-white/80 backdrop-blur-sm relative z-10">
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Type a message..."
            className="flex-1 border-0 bg-black/5 focus-visible:ring-0 focus-visible:ring-offset-0 pr-12"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            className={cn(
              "absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-md transition-colors",
              newMessage.trim() && !sending
                ? "text-blue-500 hover:bg-blue-50"
                : "text-gray-300 cursor-not-allowed"
            )}
            title="Send message"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        
        <div className="mt-2 flex items-center justify-between text-xs text-black/40">
          <div className="flex items-center space-x-2">
            <button className="hover:text-black transition-colors">
              <Smile className="w-4 h-4" />
            </button>
          </div>
          <div>
            Press <kbd className="px-1.5 py-0.5 bg-black/5 rounded text-xs font-mono">Enter</kbd> to send
          </div>
        </div>
      </div>
    </div>
  );
};
